import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ShoppingCart } from "lucide-react";
import { ThreadwayAnchor } from "@/components/atmosphere/Atmosphere";
import { RedactedText } from "@/components/redaction/RedactedText";
import { FragmentProbe } from "@/components/secrets/FragmentProbe";
import { useSecrets } from "@/context/SecretsContext";
import { useStore } from "@/context/StoreContext";
import { StorePicker } from "@/components/StorePicker";
import { BOOKS, SITE } from "@/data/series";

const pad = (n, len = 2) => String(n).padStart(len, "0");

const formatClock = (ms) => {
  const total = Math.max(0, ms);
  const h = Math.floor(total / 3600000) % 24;
  const m = Math.floor(total / 60000) % 60;
  const s = Math.floor(total / 1000) % 60;
  const cs = Math.floor((total % 1000) / 10);
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(cs)}`;
};

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Station standard start point: 02:17:00.00
const BASE_MS = 2 * 3600000 + 17 * 60000;

export const Hero = () => {
  const { isEggActive, markEggFound, triggerMemorial } = useSecrets();
  const { code, kindleUrl, paperbackUrl, urlFor } = useStore();
  const bookTwo = BOOKS.find((b) => b.number === 2);
  const bookTwoUrl = bookTwo?.asin ? urlFor(bookTwo.asin) : SITE.bookTwoUrl;
  const [now, setNow] = useState(BASE_MS);
  const [offset, setOffset] = useState(0);
  const [dropout, setDropout] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const startRef = useRef(Date.now());
  const sentinelRef = useRef(null);
  const firedRef = useRef(false);
  const tapsRef = useRef(0);

  const onHeaderTap = useCallback(() => {
    if (!isEggActive("memorial")) return;
    tapsRef.current += 1;
    if (tapsRef.current >= 6) {
      tapsRef.current = 0;
      markEggFound("memorial");
      triggerMemorial();
    }
  }, [isEggActive, markEggFound, triggerMemorial]);

  useEffect(() => {
    const id = setInterval(() => {
      setNow(BASE_MS + (Date.now() - startRef.current));
    }, 50);
    return () => clearInterval(id);
  }, []);

  const triggerNullSix = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    const reduced = reducedMotion();
    if (reduced) {
      setOffset(6130);
      setRecorded(true);
      return;
    }
    setDropout(true);
    window.setTimeout(() => {
      setDropout(false);
      setOffset(6130);
      setRecorded(true);
    }, 520);
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          triggerNullSix();
          io.disconnect();
        });
      },
      { rootMargin: "0px 0px -35% 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [triggerNullSix]);

  return (
    <section
      className="hero-atmosphere relative z-10 overflow-hidden border-b border-border"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-6xl px-4 pb-16 pt-14 sm:px-6 sm:pb-24 sm:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-16">
          {/* ---------------- copy ---------------- */}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="stamp stamp-open">Books one &amp; two out now</span>
              <span className="eyebrow">{SITE.classification}</span>
            </div>

            <h1 className="mt-6 text-[2.6rem] font-semibold leading-[0.98] sm:text-6xl lg:text-[4.4rem]">
              The
              <br />
              Fractured
              <br />
              <span className="text-[hsl(32_92%_62%)]">Five</span>
            </h1>

            <p className="mt-7 max-w-xl text-[15px] leading-relaxed text-[hsl(210_14%_84%)] sm:text-[17px]">
              {SITE.logline}
            </p>

            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <RedactedText text={SITE.subLogline} />
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={kindleUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-button"
                className="group inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-primary px-6 font-mono text-[11px] uppercase tracking-[0.18em] text-primary-foreground shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
              >
                <ShoppingCart className="h-4 w-4" />
                Book One on Kindle
                <span
                  aria-hidden="true"
                  className="text-primary-foreground/70"
                >
                  · {code}
                </span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
              </a>

              <Link
                to="/read"
                data-testid="hero-read-opening-button"
                className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-[12px] border border-border bg-[hsl(220_16%_10%)] px-6 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground transition-colors duration-150 hover:border-[hsl(174_72%_42%/0.5)] hover:bg-[hsl(220_16%_13%)]"
              >
                <BookOpen className="h-4 w-4" />
                Read the first six seconds
              </Link>
            </div>

            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(215_10%_50%)]">
              Prefer paper?{" "}
              <a
                href={paperbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-paperback-link"
                className="text-[hsl(32_92%_66%)] underline decoration-[hsl(32_92%_66%/0.45)] underline-offset-4 transition-colors duration-150 hover:text-[hsl(32_92%_76%)]"
              >
                Book One in paperback
              </a>
            </p>

            <div className="mt-3">
              <StorePicker className="-ml-1.5" />
            </div>

            <p className="mt-5 font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(215_10%_50%)]">
              Book One out now · ebook and paperback · opening chapter free
              <span className="block">
                Book II —{" "}
                <a
                  href={bookTwoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-book-two-link"
                  className="text-[hsl(32_92%_66%)] underline decoration-[hsl(32_92%_66%/0.45)] underline-offset-4 transition-colors duration-150 hover:text-[hsl(32_92%_76%)]"
                >
                  The Weight of Blood
                </a>{" "}
                out now ·{" "}
                <a
                  href="#waitlist"
                  data-testid="hero-join-signal-button"
                  className="text-[hsl(174_62%_64%)] underline decoration-[hsl(174_72%_42%/0.5)] underline-offset-4 transition-colors duration-150 hover:text-[hsl(174_62%_74%)]"
                >
                  join the signal
                </a>{" "}
                for the rest
              </span>
            </p>
          </div>

          {/* ---------------- telemetry panel ---------------- */}
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden">
              <ThreadwayAnchor className="h-[125%] w-[125%] max-w-none opacity-80" />
            </div>

            <div
              className={`panel relative overflow-hidden ${
                dropout ? "is-dropout" : ""
              }`}
              data-testid="hero-telemetry-panel"
            >
              <div className="flex items-center justify-between gap-3 border-b border-border bg-[hsl(220_18%_8%)] px-4 py-2.5">
                <button
                  type="button"
                  onClick={onHeaderTap}
                  data-testid="hero-panel-header"
                  className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-150 hover:text-[hsl(32_80%_66%)]"
                >
                  Aster Reach · station standard
                </button>
                <span
                  className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] ${
                    recorded
                      ? "text-[hsl(2_80%_68%)]"
                      : "text-[hsl(174_62%_60%)]"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      recorded
                        ? "bg-[hsl(2_72%_56%)]"
                        : "bg-[hsl(174_72%_46%)] animate-pulse"
                    }`}
                  />
                  {recorded ? "integrity fault" : "nominal"}
                </span>
              </div>

              <div className="px-4 py-5 sm:px-5">
                <p className="eyebrow">Mesh clock</p>
                <p
                  data-testid="hero-clock"
                  className={`mt-1 font-mono text-3xl tabular-nums tracking-tight sm:text-[2.6rem] ${
                    dropout ? "text-[hsl(2_80%_66%)]" : "text-foreground"
                  }`}
                >
                  {dropout ? "--:--:--.--" : formatClock(now + offset)}
                </p>

                <div
                  className="mt-4 min-h-[2.75rem] rounded-[8px] border px-3 py-2"
                  data-testid="null-six-event"
                  style={
                    recorded
                      ? {
                          borderColor: "hsl(2 72% 46% / 0.5)",
                          background: "hsl(2 72% 46% / 0.08)"
                        }
                      : {
                          borderColor: "hsl(220 12% 18%)",
                          background: "hsl(220 18% 8%)"
                        }
                  }
                >
                  {recorded ? (
                    <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(2_80%_70%)]">
                      Null six event recorded — 6.13 s unaccounted
                      <span className="block text-[hsl(215_10%_58%)]">
                        all independent buffers agree
                      </span>
                    </p>
                  ) : (
                    <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(215_10%_55%)]">
                      Continuity monitor active
                      <span className="block">no discontinuity detected</span>
                    </p>
                  )}
                </div>

                <dl className="mt-5 space-y-0">
                  {SITE.telemetry.map((row, i) => {
                    const isLink = row.label === "Link classification";
                    const content = (
                      <>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                          {row.label}
                        </dt>
                        <dd className="font-mono text-[11px] tabular-nums text-[hsl(210_16%_86%)]">
                          <RedactedText text={row.value} />
                        </dd>
                      </>
                    );
                    const cls = `flex items-baseline justify-between gap-3 py-2.5 ${
                      i > 0 ? "border-t border-border" : ""
                    }`;
                    return isLink ? (
                      <FragmentProbe
                        key={row.label}
                        fragmentId="f1"
                        as="div"
                        label="Probe the Link classification"
                        className={cls}
                      >
                        {content}
                      </FragmentProbe>
                    ) : (
                      <div key={row.label} className={cls}>
                        {content}
                      </div>
                    );
                  })}
                </dl>
              </div>
            </div>

            <figure className="mt-6 border-l-2 border-[hsl(32_92%_62%/0.5)] pl-4">
              <blockquote className="text-sm italic leading-relaxed text-[hsl(210_14%_82%)]">
                “<RedactedText text={SITE.pullQuote.text} />”
              </blockquote>
              <figcaption className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                <RedactedText text={SITE.pullQuote.attribution} />
              </figcaption>
            </figure>
          </div>
        </div>
      </div>

      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
    </section>
  );
};
